import { AbstractMesh, Material, Matrix, Mesh, MeshBuilder, Observer, Quaternion, Ray, TransformNode, Vector3 } from '@babylonjs/core';
import { Observable } from '@babylonjs/core/Misc/observable';
import { BabylonjsModule } from '@modules/babylonjs';
import { BaseSystem, SystemOperationDefinition, Zed } from '@modules/core';
import { createEntityFactory, EasingFunctionType, EasingModeType, GLTFLoader, playAnimation, toAngle, toRadian } from '@modules/standard';
import { World } from 'miniplex';

import { Entity as BaseEntity, DongEntity, DongSystem } from '..';
import { DanjiEntity, getUnitDetail, onCameraViewTypeChanged, onUnitActivated, onUnitViewStateChanged, TourDanjiCameraAPI } from '../..';
import { DanjiHoData, DanjiHoTransformData, UnitDetailData } from '../../APIResponse';
import { LayerMask, MAX_DISTANCE_FROM_UNIT_WINDOW } from '../../Constants';
import { MaterialManager, MaterialType } from '../../graphic';
import { LightManager } from '../../LightManager';
import { Unit } from '../../utiles/Utils';
import { UnitResponse } from '../component/response/Unit';

import { UnitEntity, UnitSystem } from './UnitSystem';

const UNDEFINED_ANGLE = -1 * toRadian;

export type UnitWindowData = {
  name: string;
  transform: TransformNode;
};

export type UnitModelEntity = {
  Selected?: true;
  Model?: {
    src: string;
    modelName: string;
  };
  ModelLoading?: true;
  Initialized?: true;
  UnitModel?: {
    floor: number;
  };
  RoomTypeId?: number;
  Windows?: UnitWindowData[];
  ModelTransform?: TransformNode;
};

type Entity = UnitModelEntity & DanjiEntity & DongEntity & UnitEntity & BaseEntity;

const BASE_SCALE = 0.01;
const FLOOR_MARGIN = 0.05;
const BASE_WALL_HEIGHT = 3;

interface UnitModelTransform {
  angle: number;
  xOffset: number;
  yOffset: number;
  zOffset: number;
  scale: Vector3;
  basis: Vector3;
  isAngleInitialized: boolean;
}
export class UnitModelSystem implements BaseSystem {
  static readonly INJECTABLE_TOKEN = Symbol.for('UnitModelSystem');

  private readonly scene;
  private readonly gltfLoader;
  private readonly rootTransform;
  private readonly loadedModels;
  private readonly world;
  private readonly materialManager;
  private readonly lightManager;
  private readonly cameraAPI;
  private readonly withDanjiId;
  private readonly withDongInit;
  private readonly withUnitInit;
  private readonly withUnitDataMesh;
  private readonly selectedUnitModel;
  private readonly buildingGroundHeights = new Map<string, number>();
  private readonly unitModelEnities = new Map<number, Entity[]>();
  private readonly ray: Ray;
  private readonly fakeWindowViewMesh;
  private readonly withUnitData;

  // NOTE: 기존 api에서 받아오는 원천 데이터 형식이 babylonjs에서 사용하는 형식과 다르기 때문에 별도로 저장
  // unitTransformSourceData -> unitModelTransforms로 변환하여 사용
  private readonly unitTransformSourceData = new Map<string, DanjiHoTransformData[]>();
  private readonly unitModelTransforms = new Map<number, UnitModelTransform[]>();

  private readonly createEntity;
  onUnitActivatedObserver: Observer<UnitResponse | undefined> | undefined = undefined;

  // Add new observable for unit enter/exit events
  public readonly onUnitViewStateChanged = new Observable<{ isEntering: boolean; isHideUnitName?: boolean }>();

  constructor({
    [Zed.WORLD_TOKEN]: world,
    [BabylonjsModule.INJECTABLE_TOKEN]: babylonjs,
    [GLTFLoader.INJECTABLE_TOKEN]: gltfLoader,
    [DongSystem.INJECTABLE_TOKEN]: dongSystem,
    [UnitSystem.INJECTABLE_TOKEN]: unitSystem,
    [MaterialManager.INJECTABLE_TOKEN]: materialManager,
    [LightManager.INJECTABLE_TOKEN]: lightManager,
    [TourDanjiCameraAPI.INJECTABLE_TOKEN]: cameraAPI,
  }: {
    [Zed.WORLD_TOKEN]: World<Entity>;
    [BabylonjsModule.INJECTABLE_TOKEN]: BabylonjsModule;
    [GLTFLoader.INJECTABLE_TOKEN]: GLTFLoader;
    [DongSystem.INJECTABLE_TOKEN]: DongSystem;
    [UnitSystem.INJECTABLE_TOKEN]: UnitSystem;
    [MaterialManager.INJECTABLE_TOKEN]: MaterialManager;
    [LightManager.INJECTABLE_TOKEN]: LightManager;
    [TourDanjiCameraAPI.INJECTABLE_TOKEN]: TourDanjiCameraAPI;
  }) {
    this.scene = babylonjs.scene;
    this.gltfLoader = gltfLoader;
    this.rootTransform = new TransformNode('UnitModels');
    this.materialManager = materialManager;

    this.lightManager = lightManager;
    this.cameraAPI = cameraAPI;
    this.withDanjiId = world.with('DanjiId');
    this.withDongInit = world.with('DongInit', 'DongData', 'TransformNode');
    this.withUnitInit = world.with('UnitInit', 'UnitData', 'parent');
    this.withUnitDataMesh = world.with('UnitData', 'TransformNode');
    this.withUnitData = world.with('UnitData');
    this.loadedModels = world.with('ModelTransform', 'Windows');

    this.world = world;
    this.selectedUnitModel = world.with('Selected');

    this.createEntity = createEntityFactory(this.world);

    this.operations = [
      {
        group: 'beforeRender',
        run: this.generateUnitModelTransforms,
        after: [dongSystem['createDongInit'], unitSystem['updateUnitMesh']],
      },
    ];
    this.ray = new Ray(Vector3.ZeroReadOnly, Vector3.ZeroReadOnly, MAX_DISTANCE_FROM_UNIT_WINDOW);
    this.fakeWindowViewMesh = this.generateFakeWindowView();
  }

  private createUnitModelEntity(unitData: DanjiHoData) {
    const danjiId = this.withDanjiId.first!.DanjiId;
    const unitTypeId = unitData.meta.룸타입id;
    return Array.from({ length: unitData.meta.층갯수 }, (_, i) => {
      const name = `UnitModel_${unitTypeId}_Level${i}`;
      return this.createEntity({
        Model: {
          src: `${process.env['ASSETS_URL']}/danji/units/${danjiId}_${unitTypeId}_Level${i}.glb`,
          modelName: name,
        },
        UnitModel: {
          floor: i,
        },
        RoomTypeId: unitTypeId,
      });
    });
  }

  private generateFakeWindowView() {
    const textureWidth = 1920;
    const textureHeight = 1080;
    const plane = MeshBuilder.CreatePlane(
      'FakeWindowView',
      { width: 1, height: textureHeight / textureWidth },
      this.scene
    );
    plane.material = this.materialManager.createMaterial(MaterialType.FakeWindowView);
    plane.visibility = 0;

    return plane;
  }

  public generateUnitModelTransforms = () => {
    for (const entity of this.withDongInit) {
      const { DongData, TransformNode } = entity;
      const key = DongData.meta.동;
      if (!key) continue;
      this.buildingGroundHeights.set(key, TransformNode.position.y);
      this.unitTransformSourceData.set(key, DongData.transforms);
    }

    for (const entity of this.withUnitInit) {
      if (this.unitModelEnities.has(entity.UnitData.meta.룸타입id)) continue;
      const unitModelEntity = this.createUnitModelEntity(entity.UnitData);
      this.unitModelEnities.set(entity.UnitData.meta.룸타입id, unitModelEntity);
    }

    for (const entity of this.withUnitDataMesh) {
      const { UnitData } = entity;
      const unitKey = UnitData.meta.ho_id;
      if (this.unitModelTransforms.has(unitKey)) continue;

      const buildingKey = UnitData.meta.동;
      const transformDataSet = this.unitTransformSourceData.get(buildingKey);
      const buildingGroundHeight = this.buildingGroundHeights.get(buildingKey) ?? 0;

      if (transformDataSet) {
        const mesh = entity.TransformNode as Mesh;
        const boundingInfo = mesh.getBoundingInfo();
        const basis = new Vector3(
          boundingInfo.boundingSphere.center.x,
          boundingInfo.minimum.y + buildingGroundHeight,
          boundingInfo.boundingSphere.center.z
        );
        const unitHeight = UnitData.meta.높이 === 0 ? 1 : UnitData.meta.높이;
        const floorHeight = unitHeight / UnitData.meta.층갯수;
        const unitModelHeight = floorHeight - FLOOR_MARGIN * 2;
        const heightScale = unitModelHeight / BASE_WALL_HEIGHT;
        const baseOffset = unitModelHeight * 0.5 + FLOOR_MARGIN;

        this.unitModelTransforms.set(
          unitKey,
          Array.from({ length: UnitData.meta.층갯수 }, (_, floorIndex) => {
            const source = Unit.findUnitTransform(transformDataSet, {
              unitId: UnitData.meta.ho_id,
              buildingName: buildingKey,
              unitName: UnitData.meta.호,
              floorIndex,
            })!;
            const scale = source
              ? new Vector3(
                  source.Scale * (source.FlipX ? -1 : 1) * BASE_SCALE,
                  heightScale * BASE_SCALE,
                  source.Scale * (source.FlipZ ? -1 : 1) * BASE_SCALE
                )
              : Vector3.OneReadOnly.scale(BASE_SCALE);
            return {
              angle: source ? source.Angle * toRadian : UNDEFINED_ANGLE,
              xOffset: source?.OffsetX ?? 0,
              yOffset: baseOffset + floorHeight * floorIndex,
              zOffset: source?.OffsetZ ?? 0,
              scale,
              basis,
              isAngleInitialized: source && source.Angle >= 0,
            };
          })
        );
      }
    }
  };

  private setVisibleFakeWindowView = (position: Vector3, direction: Vector3) => {
    const fitToView = () => {
      const boundingInfo = this.fakeWindowViewMesh.getBoundingInfo();
      const meshSize = boundingInfo.maximum.subtract(boundingInfo.minimum);
      // TODO: fix hard coding from transitionToUnitWindowView
      const fov = this.cameraAPI.camera.fov;
      const distance = 0;
      const cameraDistance = distance + 0.5;
      const aspect = this.scene.getEngine().getAspectRatio(this.cameraAPI.camera);
      const viewHeightAtDistance = 2 * cameraDistance * Math.tan(fov / 2);
      const viewWidthAtDistance = viewHeightAtDistance * aspect;
      const scaleX = viewWidthAtDistance / meshSize.x;
      const scaleY = viewHeightAtDistance / meshSize.y;
      const windowScale = Math.max(scaleX, scaleY);

      this.fakeWindowViewMesh.position = position.add(direction.scale(distance));
      this.fakeWindowViewMesh.lookAt(this.fakeWindowViewMesh.position.add(direction));
      this.fakeWindowViewMesh.scaling.setAll(windowScale);
    };

    fitToView();

    playAnimation({
      target: this.fakeWindowViewMesh,
      property: {
        targetProperty: 'visibility',
        keyFrame: {
          duration: 0.2,
          from: 0,
          to: 1,
        },
      },
    });

    const onResize = this.scene.getEngine().onResizeObservable.add(() => {
      fitToView();
    });

    onCameraViewTypeChanged.addOnce(() => {
      this.fakeWindowViewMesh.visibility = 0;
      onResize.remove();
    });
  };

  public attachToUnit(unitData: DanjiHoData, floor = 0): UnitModelEntity[] {
    if (!unitData) return [];

    const unitModelEnities = this.unitModelEnities.get(unitData.meta.룸타입id);

    for (let floorIndex = 0; floorIndex < unitData.meta.층갯수; floorIndex++) {
      const unitModelEntity = unitModelEnities?.[floorIndex];
      const modelTransform = unitModelEntity?.ModelTransform;
      if (!modelTransform) continue;

      const transform = this.unitModelTransforms.get(unitData.meta.ho_id)?.[floorIndex];
      if (!transform) continue;

      const { xOffset, yOffset, zOffset, scale, basis, isAngleInitialized } = transform;
      modelTransform.position.x = basis.x + xOffset;
      modelTransform.position.y = basis.y + yOffset;
      modelTransform.position.z = basis.z + zOffset;
      modelTransform.scaling.x = scale.x;
      modelTransform.scaling.y = scale.y;
      modelTransform.scaling.z = scale.z;

      // NOTE: unitTransformSourceData에 회전각이 없는 경우 처리
      if (!isAngleInitialized) {
        const livingRoomWindow = unitModelEntity?.Windows?.find((w) => w.name.includes('Living'));
        // 거실 창호 방향 각도
        const livingRoomWindowRotation = livingRoomWindow?.transform.rotationQuaternion?.toEulerAngles()._y ?? 0;
        // 창호 방향을 북쪽으로 되돌린 후 '향' 만큼 추가 회전
        transform.angle = unitData.meta.향 * toRadian - livingRoomWindowRotation;
        transform.isAngleInitialized = true;
      }

      const { angle } = transform;
      modelTransform.rotationQuaternion = Quaternion.FromRotationMatrix(Matrix.RotationY(angle));
      modelTransform.computeWorldMatrix(true);

      const isActive = floorIndex === floor;
      modelTransform.getChildMeshes().forEach((mesh) => {
        mesh.layerMask = isActive ? mesh.layerMask | LayerMask.Minimap : mesh.layerMask & ~LayerMask.Minimap;
      });
      modelTransform.getChildTransformNodes().forEach((node) => {
        node.computeWorldMatrix(true);
      });
    }

    return unitModelEnities ?? [];
  }

  public enter = async (unitData: DanjiHoData, isHideUnitName?: boolean) => {
    const unit = this.withUnitData.entities.find((entity) => entity.UnitData.meta.ho_id === unitData.meta.ho_id);
    if (!unit) {
      console.error(new Error('해당 세대가 존재하지 않습니다.'), unitData);
      return;
    }

    const entites = await this.getEntities(unit.UnitData.meta.룸타입id);
    if (entites.length === 0) {
      console.error(new Error('평면도가 존재하지 않습니다.'), unitData);
      return;
    }
    let position: Vector3 | undefined = undefined;
    let direction: Vector3 | undefined = undefined;
    const floorIndex = 0;
    const unitModels = this.attachToUnit(unit.UnitData, floorIndex);
    const unitModel = unitModels[floorIndex];
    const viewType = unitModel?.Windows ? 'UnitWindowView' : 'FakeUnitWindowView';
    const window = unitModel?.Windows?.find((window) => window.name.includes('Living'));
    if (window && window.transform) {
      direction = window.transform.forward.normalize();
      this.ray.origin = window.transform.getAbsolutePosition();
      this.ray.direction = direction.negate();
      const pickResult = this.scene.pickWithRay(this.ray, (mesh) => {
        if (mesh.layerMask === LayerMask.Default) return false;
        return (mesh.layerMask & LayerMask.UnitCollider) === LayerMask.UnitCollider;
      });
      const offset = pickResult?.distance ? pickResult.distance - 0.3 : MAX_DISTANCE_FROM_UNIT_WINDOW;
      position = window.transform.getAbsolutePosition().add(direction.negate().scale(offset));
    } else {
      // window가 없는 경우 unitData의 향 각도를 사용하여 direction 설정
      const angle = unitData.meta.향 * toRadian;
      direction = Vector3.Forward()
        .rotateByQuaternionAroundPointToRef(
          Quaternion.FromRotationMatrix(Matrix.RotationY(angle)),
          Vector3.Zero(),
          new Vector3()
        )
        .normalize();

      // unitModelTransforms를 사용하여 unit의 중심점 계산
      const unitTransforms = this.unitModelTransforms.get(unitData.meta.ho_id);
      if (!unitTransforms || unitTransforms.length === 0) {
        console.error('Unit transforms not found');
        return;
      }

      // 첫 번째 층의 transform을 기준으로 중심점 계산
      const firstFloorTransform = unitTransforms[0];
      if (!firstFloorTransform) {
        console.error('First floor transform not found');
        return;
      }

      const unitCenter = new Vector3(
        firstFloorTransform.basis.x + firstFloorTransform.xOffset,
        firstFloorTransform.basis.y + firstFloorTransform.yOffset,
        firstFloorTransform.basis.z + firstFloorTransform.zOffset
      );

      this.ray.origin = unitCenter;
      this.ray.direction = direction.negate();

      // ray를 쏘아 hit 지점 찾기
      const pickResult = this.scene.pickWithRay(this.ray, (mesh) => {
        if (mesh.layerMask === LayerMask.Default) return false;
        return (mesh.layerMask & LayerMask.UnitCollider) === LayerMask.UnitCollider;
      });

      // hit 지점이 있으면 해당 지점을 position으로 사용, 없으면 기본 거리 사용
      const offset = pickResult?.distance ? pickResult.distance - 0.3 : MAX_DISTANCE_FROM_UNIT_WINDOW;
      position = unitCenter.add(direction.negate().scale(offset));
    }
    if (!position || !direction) {
      return;
    }

    onUnitViewStateChanged.notifyObservers({ isEntering: true, isHideUnitName: isHideUnitName ?? false });

    await this.cameraAPI.transitionToUnitWindowView(position, direction, viewType).then(() => {
      if (viewType === 'FakeUnitWindowView') {
        this.setVisibleFakeWindowView(position.add(direction.scale(0.5)), direction);
      }
      const unitDetail =
        unit.UnitDetail ??
        getUnitDetail(unitData.meta.ho_id)
          .then((detail) => {
            this.world.update(unit, 'UnitDetail', detail);
            return detail;
          })
          .catch((reason) => {
            console.warn(new Error('세대 정보가 존재 하지 않습니다.', { cause: reason }));
            const detail = {
              dong: unit.UnitData.meta.동,
              hoId: unit.UnitData.meta.ho_id,
              ho: unit.UnitData.meta.호,
              danjiRoomTypeId: unit.UnitData.meta.룸타입id,
            } as UnitDetailData;
            this.world.update(unit, 'UnitDetail', detail);
            return detail;
          });
      Promise.resolve(unitDetail).then((detail) => {
        this.activate({
          buildingName: unit.UnitData.meta.동,
          unitName: unit.UnitData.meta.호,
          unitMeshes: unitModels?.map((m) => m.ModelTransform as Mesh),
          unitMetadata: unit.UnitData.meta,
          unitDetail: detail,
          floorIndex,
          windowPoints: unitModels!.map((m) => m.Windows!) ?? undefined,
        });
      });
    });
  };

  public exit = async () => {
    onUnitViewStateChanged.notifyObservers({ isEntering: false });

    this.deactivate();

    await this.cameraAPI.popCameraView({
      seconds: 1.0,
      easingFunction: EasingFunctionType.SineEase,
      easingMode: EasingModeType.In,
    });
  };

  private activate(response: UnitResponse) {
    this.lightManager.subLight.setEnabled(true);

    const entities = [...this.loadedModels.entities].filter((e) => e.RoomTypeId === response?.unitMetadata.룸타입id);
    entities.forEach((e) => {
      this.lightManager.shadowGenerator.normalBias = 0.02;
      this.world.addComponent(e, 'Selected', true);
      e.ModelTransform?.setEnabled(true);
      if (e.UnitModel?.floor === response.floorIndex) {
        e.ModelTransform?.getChildMeshes().forEach((mesh: Mesh | AbstractMesh) => {
          mesh.layerMask = mesh.layerMask | LayerMask.Minimap;
          if (this.isShadowCastTarget(mesh.material?.name)) {
            this.lightManager.addMainLightRenderList(mesh, true);
            this.lightManager.addShadowCaster(mesh, true);
            mesh.receiveShadows = true;
          }
        });
      } else {
        e.ModelTransform?.getChildMeshes().forEach((mesh: Mesh | AbstractMesh) => {
          mesh.layerMask = mesh.layerMask & ~LayerMask.Minimap;
        });
      }
    });

    if (onUnitActivated.hasObservers()) {
      onUnitActivated.notifyObservers(response);
    }
  }

  private deactivate() {
    this.lightManager.subLight.setEnabled(false);

    for (const e of this.selectedUnitModel) {
      this.world.removeComponent(e, 'Selected');
      e.ModelTransform?.setEnabled(false);
      e.ModelTransform?.getChildMeshes().forEach((mesh: Mesh | AbstractMesh) => {
        if (this.isShadowCastTarget(mesh.material?.name)) {
          this.lightManager.removeMainLightRenderList(mesh, true);
          this.lightManager.removeShadowCaster(mesh, true);
          mesh.receiveShadows = false;
        }
      });
    }

    if (onUnitActivated.hasObservers()) {
      onUnitActivated.notifyObservers(undefined);
    }
  }

  public changeFloor(floorIndex: number) {
    for (const e of this.selectedUnitModel) {
      if (e.UnitModel?.floor === floorIndex) {
        e.ModelTransform?.getChildMeshes().forEach((mesh: Mesh | AbstractMesh) => {
          mesh.layerMask = mesh.layerMask | LayerMask.Minimap;
          if (this.isShadowCastTarget(mesh.material?.name)) {
            this.lightManager.addMainLightRenderList(mesh, true);
            this.lightManager.addShadowCaster(mesh, true);
            mesh.receiveShadows = true;
          }
        });
      } else {
        e.ModelTransform?.getChildMeshes().forEach((mesh: Mesh | AbstractMesh) => {
          mesh.layerMask = mesh.layerMask & ~LayerMask.Minimap;
          if (this.isShadowCastTarget(mesh.material?.name)) {
            this.lightManager.removeMainLightRenderList(mesh, true);
            this.lightManager.removeShadowCaster(mesh, true);
            mesh.receiveShadows = false;
          }
        });
      }
    }
  }

  private isShadowCastTarget(materialType?: string): boolean {
    if (!materialType) return false;
    if (
      [
        MaterialType.UnitWallPaper,
        MaterialType.UnitFloorLivingroom,
        MaterialType.UnitFloorRoom,
        MaterialType.UnitFloorBath,
        MaterialType.UnitFloorBalcony,
        MaterialType.UnitFloorGate,
        MaterialType.UnitWindowFrame,
        MaterialType.UnitWindowNewFrame,
        MaterialType.UnitDoor,
      ].includes(materialType as MaterialType)
    ) {
      return true;
    }
    return false;
  }

  public async getEntities(roomTypeId: number): Promise<Entity[]> {
    const entities = this.unitModelEnities.get(roomTypeId);
    if (!entities) return [];

    const loadedEntities: Entity[] = [];

    for (const entity of entities) {
      const { Model } = entity;
      if (!Model) continue;
      if (entity.ModelTransform) {
        loadedEntities.push(entity);
      } else {
        try {
          await this.loadUnitModel(entity, Model);
          loadedEntities.push(entity);
        } catch (error) {
          console.error(`[UnitModelSystem] ${Model.modelName} loading error: `, error);
        }
      }
    }

    return loadedEntities;
  }

  private loadUnitModel(entity: Entity, Model: { modelName: string; src: string }): Promise<void> {
    if (entity.ModelTransform) return Promise.reject(new Error('Model already loaded'));
    this.world.addComponent(entity, 'ModelLoading', true);

    return new Promise((resolve) => {
      const task = this.gltfLoader.assetsManager.addMeshTask(Model.modelName, '', Model.src, '');
      task.run(
        this.scene,
        () => {
          console.time(`[UnitModelSystem] ${Model.modelName}`);
          const tempMaterials = new Set<Material>();
          const tempWindows = new Array<UnitWindowData>();
          const windowPoints = task.loadedTransformNodes.find((node) => node.name === 'WindowPoints');
          let modelRoot: AbstractMesh | undefined = undefined;
          if (windowPoints) {
            windowPoints.getChildren().forEach((child) => {
              const transform = child as TransformNode;
              if (!transform) return;

              tempWindows.push({
                name: transform.name,
                transform,
              });
            });
          }
          this.world.addComponent(entity, 'Windows', tempWindows);

          task.loadedMeshes.forEach((mesh) => {
            if (mesh.name === '__root__') {
              mesh.setEnabled(false);
              mesh.name = Model.modelName;
              mesh.parent = this.rootTransform;
              modelRoot = mesh;
            }

            if (!mesh.material) return;

            const materialType = MaterialTypeMap[mesh.material.name as keyof typeof MaterialTypeMap];
            if (!materialType) {
              console.error(new Error(`Unknown material type: ${mesh.material.name}`));
              return;
            }

            tempMaterials.add(mesh.material);

            if (materialType === MaterialType.UnitWallPaper) {
              mesh.layerMask = LayerMask.UnitCollider;
            } else if (materialType === MaterialType.UnitWindowGlass) {
              mesh.setEnabled(false);
            }

            mesh.material = this.materialManager.createMaterial(materialType);
            mesh.material.sideOrientation = Mesh.DOUBLESIDE;
            mesh.material.backFaceCulling = false;
          });

          tempMaterials.forEach((material) => {
            material.dispose();
          });

          console.timeEnd(`[UnitModelSystem] ${Model.modelName}`);
          this.world.addComponent(entity, 'ModelTransform', modelRoot);
          this.world.removeComponent(entity, 'ModelLoading');

          resolve();
        },
        () => {
          const modelTransform = new TransformNode(Model.modelName);
          modelTransform.parent = this.rootTransform;
          this.world.addComponent(entity, 'ModelTransform', modelTransform);
          resolve();
        }
      );
    });
  }
  readonly operations: SystemOperationDefinition[] = [];
}

const MaterialTypeMap = {
  '7b5323a61cf4f3b4bbd7ea34490974d4': MaterialType.UnitWallEdge,
  e10de313e80a5e64e8d3dfd7bfcd0dbc: MaterialType.UnitWallPaper,
  fa0785ead89314b3493353091dd01228: MaterialType.UnitBath,
  c15f694023cf54de4b0ac0009dddcdb6: MaterialType.UnitBedLargeFrame,
  '4574177233c0be346bed26ccb0d6771c': MaterialType.UnitBedLargeMattress,
  e88f2810e75a48d4d8c8fb2c8b3e8f21: MaterialType.UnitBedSmallFrame,
  '930b29f140315d14ea02d1f66d8380ea': MaterialType.UnitBedSmallMattress,
  '7e8694c2053f94d349f7d33e6ee106e2': MaterialType.UnitChestBody,
  d1562ca120d1416419398ac4c8b497b3: MaterialType.UnitChestPlate,
  '29ad5617c73c436408340351121493b3': MaterialType.UnitSinkbasin,
  ff661b42b5a7b44d18b9df2e4769535a: MaterialType.UnitChair,
  a44a0ffc9b423b143b90fa65e7a54af0: MaterialType.UnitCouch,
  e8928984b6cf58643a6b3b11090cdde4: MaterialType.UnitDoor,
  cab063c151f336d46b081d8954c0d285: MaterialType.UnitFloorBalcony,
  '599c4a81495a40c46a08f1563161c3a4': MaterialType.UnitFloorBath,
  '34efe94d201117a47bd73d8191bf67f8': MaterialType.UnitFloorGate,
  '7fef8326d7b6a14458a5f7d65383bcb3': MaterialType.UnitFloorLivingroom,
  a7cc73db7e3e96947862f219156e7c2e: MaterialType.UnitFloorRoom,
  '1578708faa5f24993b4c0bbab621914d': MaterialType.UnitTable,
  '436dc347aab074d1097d324fac428764': MaterialType.UnitToilet,
  d50f36bb60bd843869238101dd85428d: MaterialType.UnitWashbasin,
  afb198a6dc3da40f59623a99f2f125f3: MaterialType.UnitWatertap,
  f1b7f0acdff974f09999535743381f02: MaterialType.UnitWindowGlass,
  '31c4a190075e6c44894f956279219330': MaterialType.UnitWindowFrame,
  '5d15d497be900ac46a05728be648a06e': MaterialType.UnitWindowNewFrame,
};
